import {useState} from "react";
import {ArrowLeft, ArrowRight} from "lucide-react";
import {NormResources} from "./NormResources";
import {amount, type Schema, type Work} from "./api";
import {normCode, normNotes} from "./normCode";

type Candidate = Schema["NormRecommendation"];

export function NormAlternatives({work, candidates, canEdit, choose}: {
  work: Work; candidates: Candidate[]; canEdit: boolean; choose: (candidate: Candidate) => void;
}) {
  const [index, setIndex] = useState(0);
  const current = Math.min(index, candidates.length - 1);
  const candidate = candidates[current];
  if (!candidate) return null;
  const applied = work.norms.some(row => row.norm.id === candidate.norm.id
    && row.quantity_work_id === candidate.quantity_work_id
    && [...row.work_ids].sort().join() === [...candidate.work_ids].sort().join());
  return <details className="norm-alternatives" open={!work.norms.length}>
    <summary>Подбор расценки <span>Вариантов: {candidates.length}</span></summary>
    <div className="norm-project-description">
      <small>В проекте</small><p>{work.description}</p>
      <small>{work.group}{work.mount ? ` · ${work.mount}` : ""} · {amount(work.quantity)} {work.unit}</small>
    </div>
    {candidates.length > 1 && <div className="norm-slider">
      <div className="norm-slider-navigation">
        <button type="button" className="quiet" aria-label="Предыдущая расценка" disabled={current === 0} onClick={() => setIndex(current - 1)}><ArrowLeft size={17}/></button>
        <span aria-live="polite">Вариант {current + 1} из {candidates.length}</span>
        <button type="button" className="quiet" aria-label="Следующая расценка" disabled={current === candidates.length - 1} onClick={() => setIndex(current + 1)}><ArrowRight size={17}/></button>
      </div>
      <input type="range" aria-label="Варианты расценок" aria-valuetext={`Вариант ${current + 1} из ${candidates.length}: ${normCode(candidate.norm.code)}`} min={0} max={candidates.length - 1} step={1} value={current} onChange={e => setIndex(Number(e.target.value))}/>
    </div>}
    <article className="norm-candidate" key={`${candidate.norm.id}:${candidate.quantity_work_id}:${candidate.work_ids.join()}`}>
      <div className="norm-candidate-heading"><strong>{normCode(candidate.norm.code)}</strong><span>{applied ? "Выбрана" : candidate.origin === "analysis" ? "Рекомендация анализа" : candidate.origin === "applied" ? "Текущее применение" : "Кандидат из базы"}</span></div>
      <h4>{candidate.norm.title}</h4>
      <p className="muted">{candidate.catalog_label} · единица нормы: {candidate.norm.unit || "не указана"}</p>
      <p>{candidate.reason}</p>
      {!!candidate.warnings?.length && <ul className="norm-candidate-warnings">{candidate.warnings.map(note => <li key={note}>{note}</li>)}</ul>}
      <details className="norm-candidate-content"><summary>Состав работ и условия применения</summary>
        {candidate.norm.operations.length ? <ol>{candidate.norm.operations.map((operation, i) => <li key={i}>{operation}</li>)}</ol> : <p>Состав работ в базе не раскрыт.</p>}
        <p>{candidate.norm.technical || "Техническая часть не загружена."}</p>
      </details>
      <NormResources normId={candidate.norm.id}/>
      {candidate.work_ids.length > 1 && <p className="muted">Комплексная норма · работ в составе: {candidate.work_ids.length}. Перед выбором покажем весь список.</p>}
      {canEdit && <button type="button" className="primary" disabled={applied || candidate.can_apply === false} onClick={() => choose(candidate)}>{applied ? "Уже выбрана" : "Выбрать эту расценку"}</button>}
    </article>
    {normNotes(work.suggestion.norm_notes, work.norms.length > 0).map(note => <p className="muted" key={note}>{note}</p>)}
  </details>;
}
